import { useMutation } from '@apollo/client';
import { isEmpty, kebabCase } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  AgendaAppointmentType,
  useAgendaAppointmentTypeValues,
} from '../../../../data/data/agenda/select-values';
import { ConversationSelectType } from '../../../../data/data/conversations/enums';
import { useConversationSelectTypeValues } from '../../../../data/data/conversations/select-values';
import {
  ChatMessageActionType,
  ChatMessagePostedByType,
  ChatMessageType,
} from '../../../../data/data/enums';
import { useHealthProfessionalItems } from '../../../../data/data/health-professionals/use-health-professional-items';
import { useAvailableAppointmentHonorificBlockMinuteValues } from '../../../../data/data/honorific-block-times/use-available-appointment-honorific-block-minute-values';
import { usePatientConversationItems } from '../../../../data/data/patients/use-patient-conversation-items';
import { usePatientItem } from '../../../../data/data/patients/use-patient-item';
import { ReminderMessageType } from '../../../../data/data/reminders/enums';
import {
  useReminderFrequencyValues,
  useReminderMessageTypeValues,
} from '../../../../data/data/reminders/select-values';
import { useReminderItem } from '../../../../data/data/reminders/use-reminder-item';
import { getReminderDefaultSendDate } from '../../../../data/data/utils';
import {
  ConsultType,
  ConversationItemDataFragment,
  GetReminderGroupItemsDocument,
  GetReminderItemsDocument,
  Honorific,
  MutateReminderItemDocument,
  ReminderFrequency,
  ReminderItemDataFragment,
  ReminderType,
  TypeAgenda,
} from '../../../../data/types/api/graphql';
import { formatDate } from '../../../../data/utils/date.utils';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import { useNavigateWithHealthProvider } from '../../../../data/utils/router.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import {
  ChatMessage,
  ChatMessagePosition,
} from '../../../../elements/chat/chat-message/chat-message';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { InputTextAreaController } from '../../../../elements/form/controllers/input-textarea';
import {
  FormColumns,
  FormRow,
  FormSection,
} from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputCheckboxType } from '../../../../elements/form/input/input-checkbox';
import { InputSwitch } from '../../../../elements/form/input/input-switch';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Message, MessageType } from '../../../../elements/message/message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { InputAutoCompletePatientFieldController } from '../../../form/controllers/input-auto-complete-patient-field-controller';
import { InputHonorificBlockMinutesController } from '../../../form/controllers/input-honorific-block-minutes-controller/input-honorific-block-minutes-controller';
import { ReminderDateDescription } from '../../reminder-date-description';
import { ReminderDescription } from '../../reminder-description';
import { ReminderDeletePrompt } from '../reminder-delete-prompt/reminder-delete-prompt';
import { useReminderForm } from './form/use-reminder-form';

export enum ReminderModalAction {
  DEFAULT = 'DEFAULT',
  UPDATE = 'UPDATE',
}

type ReminderModalProps = {
  action?: ReminderModalAction;
  healthProviderId: string | null | undefined;
  reminderId: string | null | undefined;
  onSaved?: (reminder: ReminderItemDataFragment) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function ReminderModal({
  healthProviderId,
  reminderId,
  onSaved,
  onCancel,
  onClose,
}: ReminderModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [formStep, setFormStep] = useState<number>(1);
  const [action, setAction] = useState<ReminderModalAction>(
    reminderId ? ReminderModalAction.DEFAULT : ReminderModalAction.UPDATE
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const onHandleFormStep = useCallback((nextStep: number) => {
    setFormStep(nextStep);
  }, []);

  const { form, formIsValid } = useReminderForm();

  const { data: conversationSelectTypeData } =
    useConversationSelectTypeValues();
  const { data: reminderMessageTypeData } = useReminderMessageTypeValues();
  const { data: reminderFrequencyData } = useReminderFrequencyValues();
  const { data: appointmentTypeData } = useAgendaAppointmentTypeValues(
    AgendaAppointmentType.SIMPLE
  );

  const patientId = form.watch('patientId');

  const { data: patientItemData } = usePatientItem(patientId);

  const conversationType = form.watch('conversationType');
  const conversationId = form.watch('conversationId');
  const conversationTitle = form.watch('conversationTitle');

  const { data: reminderItemData, loading: reminderItemLoading } =
    useReminderItem(reminderId);

  const { data: patientConversationItemsData } = usePatientConversationItems({
    healthProviderId,
    patientId,
  });

  const { patientConversationItem, patientConversationItems } = useMemo(() => {
    let conversationItem: ConversationItemDataFragment | null = null;

    if (conversationType === ConversationSelectType.EXISTING) {
      const patientConversationItemData = patientConversationItemsData.find(
        option => option.id === conversationId
      );

      conversationItem = {
        id: patientConversationItemData?.id ?? '',
        title: patientConversationItemData?.title ?? '',
      } as ConversationItemDataFragment;
    }

    if (conversationType === ConversationSelectType.NEW) {
      conversationItem = {
        title: conversationTitle,
      } as ConversationItemDataFragment;
    }

    return {
      patientConversationItem: conversationItem,
      patientConversationItems: patientConversationItemsData,
    };
  }, [
    conversationType,
    conversationId,
    conversationTitle,
    patientConversationItemsData,
  ]);

  // if reminder is set set form data
  useEffect(() => {
    if (reminderItemLoading) return;
    if (reminderItemData) {
      form.reset({
        patientId: reminderItemData.patient.id,
        conversationType: ConversationSelectType.EXISTING,
        conversationId: reminderItemData.conversation.id ?? null,
        conversationTitle: reminderItemData.conversation.title ?? null,
        startDate: formatDate(reminderItemData.startDate),
        endDate: reminderItemData.endDate === null,
        frequency: reminderItemData.frequency,
        occurrence: reminderItemData.occurrence ?? 1,
        messageType:
          reminderItemData.type === ReminderType.PlanAppointment
            ? ReminderMessageType.Appointment
            : ReminderMessageType.Custom,
        message: reminderItemData.text,
        appointmentConsultTypes:
          reminderItemData.appointment?.appointment?.consultTypes ?? [],

        appointmentHealthProfessionalId:
          reminderItemData.appointment?.healthProfessional?.id,
      });

      setFormStep(2);
    }
  }, [form, intl, reminderItemData, reminderItemLoading]);

  const navigate = useNavigateWithHealthProvider();
  const onHandleChat = useCallback(() => {
    navigate(`/conversations/${reminderItemData.conversation?.id}`);
  }, [reminderItemData, navigate]);

  useEffect(() => {
    form.setValue('conversationType', ConversationSelectType.EXISTING, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [patientId, form]);

  useEffect(() => {
    if (conversationType !== ConversationSelectType.EXISTING) {
      form.setValue('conversationId', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (conversationType !== ConversationSelectType.NEW) {
      form.setValue('conversationTitle', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [conversationType, form]);

  const messageType = form.watch('messageType');

  const frequency = form.watch('frequency');

  const isMultiple = useMemo(
    () => frequency !== ReminderFrequency.Once,
    [frequency]
  );

  const onHandleFrequency = useCallback(
    (value: boolean) => {
      form.setValue(
        'frequency',
        value ? ReminderFrequency.TwoWeeks : ReminderFrequency.Once,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [form]
  );

  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');
  const occurrence = form.watch('occurrence');
  const message = form.watch('message');
  const appointmentHealthProfessionalId = form.watch(
    'appointmentHealthProfessionalId'
  );
  const appointmentConsultTypes = form.watch('appointmentConsultTypes');
  const appointmentHonorifics = form.watch('appointmentHonorifics');

  // get healthprofessional items
  const { data: healthProfessionalItemsData } = useHealthProfessionalItems(
    {
      healthProviderId,
      honorifics: appointmentHonorifics
        ? (appointmentHonorifics.map(x => x.key) as Honorific[])
        : [],
      onlyAgenda: true,
    },
    action === ReminderModalAction.DEFAULT
  );

  // reset healthproffesional if multiple honorifics are selected
  useEffect(() => {
    if (
      appointmentHonorifics &&
      appointmentHonorifics?.length > 1 &&
      appointmentHealthProfessionalId
    ) {
      form.setValue('appointmentHealthProfessionalId', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, appointmentHealthProfessionalId, appointmentHonorifics]);

  // reset honorifics if no consulttypes are selected
  useEffect(() => {
    if (
      !appointmentConsultTypes ||
      (appointmentConsultTypes.length === 0 &&
        appointmentHonorifics &&
        appointmentHonorifics.length > 0)
    ) {
      form.setValue('appointmentHonorifics', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [appointmentConsultTypes, form, appointmentHonorifics]);

  const {
    data: availableAppointmentHonorificBlockMinuteData,
    loading: availableAppointmentHonorificBlockMinuteLoading,
  } = useAvailableAppointmentHonorificBlockMinuteValues(
    {
      healthProviderId,
      internalAgenda: false,
      consultTypes: appointmentConsultTypes as ConsultType[],
    },
    action === ReminderModalAction.DEFAULT
  );

  // set selected items based on reminder data
  useEffect(() => {
    if (
      (reminderItemData?.appointment?.appointment?.honorificBlockMinutes ?? [])
        .length > 0 &&
      availableAppointmentHonorificBlockMinuteData
    ) {
      const selected =
        reminderItemData?.appointment?.appointment?.honorificBlockMinutes.map(
          x => ({ key: x.honorific, value: x.blockMinutes })
        );
      form.setValue('appointmentHonorifics', selected, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [
    availableAppointmentHonorificBlockMinuteData,
    form,
    reminderItemData?.appointment?.appointment?.honorificBlockMinutes,
    reminderItemData?.appointment?.appointment?.honorificBlockMinutes.length,
  ]);

  // get availability of selected healthprofessional
  const {
    data: healthProfessionalAvailablilty,
    loading: healthProfessionalAvailabliltyLoading,
  } = useAvailableAppointmentHonorificBlockMinuteValues(
    {
      healthProviderId,
      internalAgenda: false,
      healthProfessionalId: appointmentHealthProfessionalId,
      consultTypes: appointmentConsultTypes as ConsultType[],
    },
    appointmentHealthProfessionalId === ''
  );

  const isSelectedHealthProfessionalAvailable = useMemo(() => {
    // No availability data - return false
    if (
      !healthProfessionalAvailablilty ||
      healthProfessionalAvailablilty.length === 0
    ) {
      return false;
    }

    // No honorifics selected - return false
    if (!appointmentHonorifics || appointmentHonorifics.length === 0) {
      return false;
    }

    // For each selected honorific, check if the health professional has available blocks
    return appointmentHonorifics.some(selectedHonorific => {
      // Find matching honorific in availability data
      const availabilityForHonorific = healthProfessionalAvailablilty.find(
        availableItem => availableItem.key === selectedHonorific.key
      );

      if (!availabilityForHonorific?.values) {
        return false;
      }

      // Find the value matching the selected honorific value and check if it's available
      return availabilityForHonorific.values.some(
        timeSlot =>
          timeSlot.value === selectedHonorific.value &&
          timeSlot.available === true
      );
    });
  }, [appointmentHonorifics, healthProfessionalAvailablilty]);

  // get healthprofessional item
  const { healthProfessionalItem, healthProfessionalItems } = useMemo(
    () => ({
      healthProfessionalItem: (healthProfessionalItemsData?.items ?? []).find(
        option => option.id === appointmentHealthProfessionalId
      ),
      healthProfessionalItems: healthProfessionalItemsData?.items ?? [],
    }),
    [appointmentHealthProfessionalId, healthProfessionalItemsData]
  );

  const [mutateReminderItem, { loading: createReminderItemLoading }] =
    useMutation(MutateReminderItemDocument, {
      refetchQueries: [GetReminderGroupItemsDocument, GetReminderItemsDocument],
    });

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await mutateReminderItem({
          variables: {
            ...values,
            id: reminderId ?? null,
            resetSendDate: false, // always false
            conversationId:
              values.conversationType === ConversationSelectType.EXISTING
                ? values.conversationId
                : null,
            conversationTitle:
              values.conversationType === ConversationSelectType.NEW
                ? values.conversationTitle
                : null,
            type:
              values.messageType !== ReminderMessageType.Custom
                ? ReminderType.PlanAppointment
                : ReminderType.Custom,
            date: values.startDate,
            occurrence: !values.endDate
              ? parseFloat(values.occurrence.toString())
              : null,
            appointment:
              values.messageType !== ReminderMessageType.Custom
                ? {
                    consultTypes:
                      values.appointmentConsultTypes as ConsultType[],
                    honorifics: values.appointmentHonorifics?.map(
                      x => x.key
                    ) as Honorific[],
                    honorificBlockMinutes: values.appointmentHonorifics
                      ? values.appointmentHonorifics?.map(item => ({
                          blockMinutes: item.value,
                          honorific: item.key,
                        }))
                      : [],
                    healthProfessionalId:
                      values.appointmentHealthProfessionalId !== ''
                        ? values.appointmentHealthProfessionalId
                        : null,
                    typeAgenda: TypeAgenda.Hidden,
                  }
                : null,
          },
        });

        if (result.data?.reminderMutate.reminder) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'patient.reminder.success',
            }),
          });

          onSaved?.(result.data?.reminderMutate.reminder);

          onClose?.();
        } else {
          throw new Error(
            'An unknown error has occurred in the server respond.'
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    mutateReminderItem,
    reminderId,
    showAlertMessage,
    intl,
    onSaved,
    onClose,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  const { getFullName, getBirthDate, getFullNameAndHonorific } =
    useFormatters();

  const canDoNext = useMemo(() => {
    if (formStep === 1 && patientId) {
      if (
        conversationType === ConversationSelectType.EXISTING &&
        conversationId
      ) {
        return true;
      }
      if (
        conversationType === ConversationSelectType.NEW &&
        conversationTitle
      ) {
        return true;
      }
    }
    if (formStep === 2 && startDate) {
      return true;
    }

    if (formStep === 3) {
      if (messageType === ReminderMessageType.Custom && message) {
        return true;
      }
      if (
        messageType === ReminderMessageType.Appointment &&
        appointmentHonorifics &&
        appointmentHonorifics?.length > 0
      ) {
        return true;
      }
    }
    return false;
  }, [
    appointmentHonorifics,
    conversationId,
    conversationTitle,
    conversationType,
    formStep,
    message,
    messageType,
    patientId,
    startDate,
  ]);

  const getTitle = () => {
    if (reminderId) {
      return action === ReminderModalAction.UPDATE
        ? 'patient.reminder-update.title'
        : 'patient.reminder.title';
    }
    return 'patient.reminder-create.title';
  };

  return (
    <>
      <Modal
        testId="reminder"
        title={<CustomFormattedMessage id={getTitle()} />}
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        className="reminder-modal"
        buttons={
          action === ReminderModalAction.UPDATE ? (
            <>
              <Button
                type={ButtonType.SECONDARY}
                inverted
                onClick={() => onHandleCancel(false)}
              >
                <CustomFormattedMessage id="common.cancel" />
              </Button>

              {formStep > 1 && (
                <Button
                  inverted
                  onClick={() => {
                    if (reminderId && action === ReminderModalAction.UPDATE) {
                      if (formStep === 2) {
                        setAction(ReminderModalAction.DEFAULT);
                      } else {
                        onHandleFormStep(formStep - 1);
                      }
                    } else {
                      onHandleFormStep(formStep - 1);
                    }
                  }}
                >
                  <CustomFormattedMessage id="common.previous" />
                </Button>
              )}

              {formStep < 4 ? (
                <Button
                  disabled={!canDoNext}
                  onClick={() => onHandleFormStep(formStep + 1)}
                >
                  <CustomFormattedMessage id="common.next" />
                </Button>
              ) : (
                <Button
                  icon={
                    createReminderItemLoading
                      ? ButtonIcon.LOADING
                      : ButtonIcon.NONE
                  }
                  disabled={!formIsValid || createReminderItemLoading}
                  onClick={onHandleSubmit}
                >
                  <CustomFormattedMessage id="patient.reminder.save" />
                </Button>
              )}
            </>
          ) : (
            <>
              <Button inverted onClick={() => setDeleteModalOpen(true)}>
                <CustomFormattedMessage id="patient.reminder.button.delete" />
              </Button>
              <Button
                disabled={reminderItemLoading}
                onClick={() => setAction(ReminderModalAction.UPDATE)}
              >
                <CustomFormattedMessage id="patient.reminder.button.update" />
              </Button>
            </>
          )
        }
      >
        <FormProvider {...form}>
          {action === ReminderModalAction.UPDATE ? (
            <>
              {formStep === 1 && (
                <>
                  <FormRow>
                    <InputAutoCompletePatientFieldController
                      name="patientId"
                      label={
                        <>
                          <CustomFormattedMessage id="common.patient" />
                          <small>*</small>
                        </>
                      }
                      placeholder={intl.formatMessage({
                        id: 'common.patients-search-placeholders',
                      })}
                      option={getFullName(patientItemData)}
                      optionVariables={{ healthProviderId }}
                    />
                  </FormRow>

                  <FormSection>
                    {!patientId ? (
                      <p className="info">
                        <CustomFormattedMessage id="common.conversation.select-patient" />
                      </p>
                    ) : (
                      <>
                        <FormRow>
                          <InputCheckboxController<ConversationSelectType>
                            type={InputCheckboxType.RADIO}
                            name="conversationType"
                            label={
                              <>
                                <CustomFormattedMessage id="common.conversation.conversation-type" />
                                <small>*</small>
                              </>
                            }
                            options={conversationSelectTypeData}
                          />
                        </FormRow>

                        <hr />

                        {!conversationType ? (
                          <p className="info">
                            <CustomFormattedMessage id="common.conversation.select-conversation-type" />
                          </p>
                        ) : (
                          <>
                            {conversationType ===
                              ConversationSelectType.EXISTING && (
                              <FormRow>
                                <InputCheckboxController
                                  type={InputCheckboxType.RADIO}
                                  name="conversationId"
                                  label={
                                    <>
                                      <CustomFormattedMessage id="common.conversation.existing-conversation" />
                                      <small>*</small>
                                    </>
                                  }
                                  placeholder={intl.formatMessage({
                                    id: 'common.conversation.no-existing-conversation',
                                  })}
                                  options={patientConversationItems.map(
                                    option => ({
                                      key: option.id,
                                      title: option.title,
                                    })
                                  )}
                                />
                              </FormRow>
                            )}

                            {conversationType ===
                              ConversationSelectType.NEW && (
                              <FormRow>
                                <InputFieldController
                                  type="text"
                                  name="conversationTitle"
                                  label={
                                    <>
                                      <CustomFormattedMessage id="common.conversation.new-conversation" />
                                      <small>*</small>
                                    </>
                                  }
                                  placeholder={intl.formatMessage({
                                    id: 'common.conversation.new-conversation',
                                  })}
                                />
                              </FormRow>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </FormSection>
                </>
              )}
              {formStep === 2 && (
                <>
                  <FormRow>
                    <InputFieldController
                      icon={BaseInputIcon.DATE}
                      type="date"
                      name="startDate"
                      label={
                        <>
                          <CustomFormattedMessage id="patient.reminder.date" />
                          <small>*</small>
                        </>
                      }
                      min={formatDate(
                        new Date(new Date().getTime() + 86400000)
                      )}
                    />
                  </FormRow>

                  <FormColumns>
                    <FormRow>
                      <InputSwitch
                        name="frequency"
                        label={
                          <CustomFormattedMessage id="patient.reminder.occurrence" />
                        }
                        value={isMultiple}
                        onChange={onHandleFrequency}
                      />
                    </FormRow>

                    {isMultiple && (
                      <FormRow>
                        <InputSelectController<ReminderFrequency>
                          name="frequency"
                          label={
                            <CustomFormattedMessage id="patient.reminder.frequency" />
                          }
                          options={reminderFrequencyData}
                        />
                      </FormRow>
                    )}
                  </FormColumns>

                  {isMultiple && (
                    <FormColumns>
                      <FormRow>
                        <InputCheckboxController<boolean>
                          name="endDate"
                          label={
                            <CustomFormattedMessage id="patient.reminder.no-end-date" />
                          }
                          options={[
                            {
                              key: true,
                              title: intl.formatMessage({
                                id: 'patient.reminder.no-end-date',
                              }),
                            },
                          ]}
                        />
                      </FormRow>

                      <FormRow>
                        <InputFieldController
                          type="number"
                          name="occurrence"
                          label={
                            <CustomFormattedMessage id="patient.reminder.repetitions" />
                          }
                          disabled={endDate}
                          min={1}
                        />
                      </FormRow>
                    </FormColumns>
                  )}

                  <FormRow>
                    <InputSelectController<ReminderMessageType>
                      name="messageType"
                      label={
                        <>
                          <CustomFormattedMessage id="patient.reminder.message-type" />
                          <small>*</small>
                        </>
                      }
                      options={reminderMessageTypeData}
                    />
                  </FormRow>
                </>
              )}
              {formStep === 3 && (
                <>
                  {messageType === ReminderMessageType.Appointment && (
                    <>
                      <FormRow>
                        <InputCheckboxController<ConsultType>
                          name="appointmentConsultTypes"
                          label={
                            <>
                              <CustomFormattedMessage id="common.consult-types" />
                              <small>*</small>
                            </>
                          }
                          options={appointmentTypeData}
                          multiple
                        />
                      </FormRow>

                      <FormRow>
                        {!availableAppointmentHonorificBlockMinuteLoading && (
                          <InputHonorificBlockMinutesController
                            label={
                              <>
                                <CustomFormattedMessage id="common.honorific" />
                                <small>*</small>
                              </>
                            }
                            name="appointmentHonorifics"
                            defaultValue={reminderItemData?.appointment?.appointment?.honorificBlockMinutes.map(
                              x => ({ key: x.honorific, value: x.blockMinutes })
                            )}
                            options={
                              availableAppointmentHonorificBlockMinuteData
                            }
                          />
                        )}
                      </FormRow>

                      <FormRow>
                        <InputSelectController<string>
                          name="appointmentHealthProfessionalId"
                          label={
                            <CustomFormattedMessage id="common.health-professional" />
                          }
                          placeholder={intl.formatMessage({
                            id: 'common.health-professional-placeholder',
                          })}
                          value={getFullName(healthProfessionalItem)}
                          options={healthProfessionalItems.map(option => ({
                            key: option.id ?? '',
                            title: getFullNameAndHonorific(option),
                          }))}
                          disabled={
                            !appointmentHonorifics?.length ||
                            appointmentHonorifics?.length > 1
                          }
                        />
                        {!healthProfessionalAvailabliltyLoading &&
                          appointmentHealthProfessionalId &&
                          isSelectedHealthProfessionalAvailable === false && (
                            <Message
                              type={MessageType.WARNING}
                              message={
                                <CustomFormattedMessage id="common.honorific-block-minutes-not-available" />
                              }
                            />
                          )}
                      </FormRow>
                    </>
                  )}

                  <FormRow>
                    <InputTextAreaController
                      name="message"
                      label={
                        <CustomFormattedMessage id="patient.reminder.message" />
                      }
                      description={
                        patientItemData ? (
                          <CustomFormattedMessage
                            id="patient.reminder.message-description"
                            values={{
                              language: (
                                <CustomFormattedMessage
                                  id={`common.language.${kebabCase(
                                    patientItemData.language
                                  )}`}
                                />
                              ),
                            }}
                          />
                        ) : undefined
                      }
                      placeholder={intl.formatMessage({
                        id: 'patient.reminder.message',
                      })}
                    />
                  </FormRow>
                </>
              )}
              {formStep === 4 && (
                <>
                  <h4>
                    <CustomFormattedMessage id="common.patient" />
                  </h4>
                  <p>
                    {patientItemData ? (
                      <>
                        {getFullName(patientItemData)}
                        <br />
                        <time>{getBirthDate(patientItemData)}</time>
                      </>
                    ) : (
                      <CustomFormattedMessage id="patient.reminder.no-patient" />
                    )}
                  </p>
                  <h4>
                    <CustomFormattedMessage id="common.conversation.conversation-type" />
                  </h4>
                  <p>
                    {!isEmpty(patientConversationItem?.title) ? (
                      patientConversationItem?.title
                    ) : (
                      <CustomFormattedMessage id="patient.reminder.no-conversation-type" />
                    )}
                  </p>

                  <h4>
                    <CustomFormattedMessage id="common.reminder" />
                  </h4>
                  <p>
                    <ReminderDescription
                      data={{
                        startDate,
                        frequency,
                        noEndDate: !!endDate,
                        occurrence,
                      }}
                    />
                  </p>

                  <div className="chat-container">
                    <div className="chat-container__messages">
                      <ChatMessage
                        position={ChatMessagePosition.RIGHT}
                        messageItem={{
                          showAppointmentConfirmation: false,
                          conversationId,
                          message,
                          type:
                            messageType !== ReminderMessageType.Custom
                              ? ChatMessageType.ACTION
                              : ChatMessageType.TEXT,
                          postedBy: {
                            type: ChatMessagePostedByType.SYSTEM,
                          },
                          actions:
                            messageType === ReminderMessageType.Appointment
                              ? [
                                  {
                                    type: ChatMessageActionType.APPOINTMENT,
                                    healthProfessional: healthProfessionalItem,
                                    appointment: {
                                      honorifics: appointmentHonorifics
                                        ? (appointmentHonorifics.map(
                                            x => x.key
                                          ) as Honorific[])
                                        : [],
                                    },
                                  },
                                ]
                              : [],
                          postedAt: getReminderDefaultSendDate(startDate),
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <h4>
                <CustomFormattedMessage id="common.patient" />
              </h4>
              <p>
                {reminderItemData?.patient ? (
                  <>
                    {getFullName(reminderItemData?.patient)}
                    <br />
                    <time>{getBirthDate(reminderItemData?.patient)}</time>
                  </>
                ) : (
                  <CustomFormattedMessage id="patient.reminder.no-patient" />
                )}
              </p>
              <h4>
                <CustomFormattedMessage id="common.conversation.conversation-type" />
              </h4>
              <p>
                {!isEmpty(reminderItemData?.conversation?.title) ? (
                  <>
                    {reminderItemData?.conversation?.title}

                    <Button
                      type={ButtonType.TEXT}
                      icon={ButtonIcon.CHEVRON_RIGHT}
                      onClick={() => onHandleChat()}
                    >
                      <CustomFormattedMessage id="common.open-chat" />
                    </Button>
                  </>
                ) : (
                  <CustomFormattedMessage id="patient.reminder.no-conversation-type" />
                )}
              </p>

              <h4>
                <CustomFormattedMessage id="common.reminder" />
              </h4>
              <p>
                <ReminderDescription
                  data={{
                    startDate: reminderItemData?.startDate,
                    frequency: reminderItemData?.frequency,
                    noEndDate: isEmpty(reminderItemData?.endDate),
                    occurrence: reminderItemData?.occurrence,
                  }}
                />
              </p>

              <h4>
                <CustomFormattedMessage id="common.reminder-date.title" />
              </h4>
              <p>
                <ReminderDateDescription
                  data={{
                    lastSendDate: reminderItemData?.lastSendDate,
                    nextSendDate: reminderItemData?.nextSendDate,
                  }}
                />
              </p>

              <div className="chat-container">
                <div className="chat-container__messages">
                  <ChatMessage
                    position={ChatMessagePosition.RIGHT}
                    messageItem={{
                      showAppointmentConfirmation: false,
                      conversationId: reminderItemData?.conversation?.id,
                      message: reminderItemData?.text ?? '',
                      type:
                        reminderItemData?.type === ReminderType.PlanAppointment
                          ? ChatMessageType.ACTION
                          : ChatMessageType.TEXT,
                      postedBy: {
                        type: ChatMessagePostedByType.SYSTEM,
                      },
                      actions:
                        reminderItemData?.type === ReminderType.PlanAppointment
                          ? [
                              {
                                type: ChatMessageActionType.APPOINTMENT,
                                appointment: {
                                  honorifics:
                                    reminderItemData?.appointment?.appointment?.honorificBlockMinutes.map(
                                      x => x.honorific
                                    ) ?? [],
                                  healthProfessional:
                                    reminderItemData?.appointment
                                      ?.healthProfessional,
                                },
                              },
                            ]
                          : [],
                      postedAt: getReminderDefaultSendDate(
                        reminderItemData?.startDate
                      ),
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </FormProvider>
      </Modal>

      {deleteModalOpen && (
        <ReminderDeletePrompt
          reminderItem={reminderItemData}
          onDeleted={() => {
            onClose?.();
          }}
          onClose={() => {
            setDeleteModalOpen(false);
          }}
        />
      )}

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
